import React, { useState } from 'react';
import { X, Search as SearchIcon } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { api } from '../lib/api';
import RecipeCard from '../components/RecipeCard';
import SearchSuggest from '../components/SearchSuggest';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

export default function Search() {
  const { t } = useLang();
  const [mode, setMode] = useState('ingredients');

  // Ingredient-based search state
  const [input, setInput] = useState('');
  const [chips, setChips] = useState([]);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);

  // Name search state
  const [nameQuery, setNameQuery] = useState('');
  const [nameResults, setNameResults] = useState([]);
  const [nameSearched, setNameSearched] = useState(false);

  const addChip = () => {
    const v = input.trim();
    if (v && !chips.includes(v)) setChips([...chips, v]);
    setInput('');
  };
  const removeChip = (c) => setChips(chips.filter(x => x !== c));

  const doIngredientSearch = async () => {
    const list = [...chips, ...(input.trim() ? [input.trim()] : [])];
    const { data } = await api.post('/search/ingredients', { ingredients: list });
    setResults(data); setSearched(true); setInput('');
  };

  const doNameSearch = async () => {
    if (!nameQuery.trim()) return;
    const { data } = await api.get('/recipes', { params: { q: nameQuery.trim() } });
    setNameResults(data); setNameSearched(true);
  };

  return (
    <div className="space-y-6" data-testid="search-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{t('search')}</h1>
      </div>

      <Tabs value={mode} onValueChange={setMode} className="w-full">
        <TabsList className="grid grid-cols-2 w-full max-w-md bg-[#EFEBE1]">
          <TabsTrigger value="ingredients" data-testid="tab-ingredients">{t('searchByIngredients')}</TabsTrigger>
          <TabsTrigger value="name" data-testid="tab-name">{t('searchByName')}</TabsTrigger>
        </TabsList>

        <TabsContent value="ingredients" className="space-y-6 mt-6">
          <div className="rounded-3xl bg-white border border-black/5 p-5 space-y-4">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addChip())}
                placeholder={t('searchPlaceholder')}
                className="flex-1"
                data-testid="search-input"
              />
              <Button onClick={doIngredientSearch} data-testid="search-btn" className="rounded-full bg-[#C05A42] hover:bg-[#A04832] text-white shrink-0">
                <SearchIcon className="h-4 w-4 me-1" /> {t('searchBtn')}
              </Button>
            </div>
            {chips.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {chips.map(c => (
                  <span key={c} className="inline-flex items-center gap-1 bg-[#EFEBE1] text-[#2C2420] px-3 py-1.5 rounded-full text-sm">
                    {c}
                    <button onClick={() => removeChip(c)} className="hover:text-[#C05A42]"><X className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {searched && results.length === 0 && (
            <div className="text-center text-[#5C4D44] py-12">—</div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="search-results">
            {results.map((r, i) => (
              <div key={r.recipe.recipe_id} className="relative">
                <RecipeCard recipe={r.recipe} index={i} />
                <span className="absolute top-3 start-3 z-10 bg-[#7A8B76] text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {r.matched} {t('matchedIngr')}
                </span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="name" className="space-y-6 mt-6">
          <div className="rounded-3xl bg-white dark:bg-neutral-900 border border-black/5 dark:border-white/10 p-5">
            <div className="flex gap-2">
              <SearchSuggest
                value={nameQuery}
                onChange={setNameQuery}
                onSubmit={doNameSearch}
                placeholder={t('namePlaceholder')}
                inputTestId="name-search-input"
              />
              <Button onClick={doNameSearch} data-testid="name-search-btn" className="rounded-full bg-[#C05A42] hover:bg-[#A04832] text-white shrink-0">
                <SearchIcon className="h-4 w-4 me-1" /> {t('searchBtn')}
              </Button>
            </div>
          </div>

          {nameSearched && nameResults.length === 0 && (
            <div className="text-center text-[#5C4D44] py-12">—</div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="name-search-results">
            {nameResults.map((r, i) => <RecipeCard key={r.recipe_id} recipe={r} index={i} />)}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
